import { Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  CheckboxDescription,
  FormGroupComponent,
  FormsUtil,
  InputComponent
} from 'mbs-ui-kit';
import { PasswordFromPlan } from 'mbs-ui-shared@constants/misc-constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Administrator } from 'mbs-ui-shared@models';
import { AgentType, minimalWindowsVersionForNewAsync } from 'mbs-ui-shared@models/Computer';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { baseDateFormatWithSeconds, dateFormatWithSpaceAndSeconds } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { take } from 'rxjs/operators';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { EncryptionOptionsStepValue, EncryptionOptionsStepValueForm } from 'wizards/models/encryption-options-models';
import { RestorePointItem } from 'wizards/models/restore-point-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';
import { PasswordRecoveryHandlerComponent } from 'mbs-ui-shared@modules/password-recovery/components/password-recovery-handler/password-recovery-handler.component';

const EncryptionOptionsStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => EncryptionOptionsStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-encryption-options-step',
  templateUrl: './encryption-options-step.component.html',
  providers: [EncryptionOptionsStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    CheckboxDescription,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    ReactiveFormsModule,
    PasswordRecoveryHandlerComponent
  ]
})
export class EncryptionOptionsStepComponent extends StepBase<EncryptionOptionsStepValue> implements OnInit, OnChanges {
  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  private auth = inject(AuthFacade);
  private providerService = inject(ProviderService);
  private stepService = inject(WizardStepsService);

  @Input() resultPoint: RestorePointItem;
  @Input() backupToRestore: string;

  public readonly elementsSelector = {
    id: {
      password: 'encryption-options-password'
    },
    name: {
      recoveryAlert: 'recoveryAlert',
      nbfPasswordBlock: 'nbfPasswordBlock',
      notNBFPasswordBlock: 'notNBFPasswordBlock',
      passwordRecoveryBlock: 'passwordRecoveryBlock',
      hintBlock: 'hintBlock',
      hintTextBlock: 'hintTextBlock'
    }
  };

  public isProvider: boolean;
  public passwordRecoveryEnabled: boolean;
  public providerEmail: string;
  public recoveryDate: string;
  public showHint = false;
  public showPassword = false;
  public allowedRecovery = false;
  public InitialValue: EncryptionOptionsStepValue;
  public passwordControlFacade = new FormControl('');

  private needOldAsync = false;
  private needCheckPassword = false;
  private invalidPassword = false;
  private canCheckPassword = false;

  get showRecoveryInfo(): boolean {
    if (!this.resultPoint || this.isProvider || !this.passwordRecoveryEnabled || !this.recoveryDate) return false;

    return moment(this.resultPoint.date, baseDateFormatWithSeconds).isAfter(moment(this.recoveryDate, dateFormatWithSpaceAndSeconds));
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.needOldAsync = (+this.mainService?.backupVersionUpdated?.substring(0, 3) || 0) < minimalWindowsVersionForNewAsync;
  }

  ngOnInit(): void {
    this.initForm();

    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user: Administrator) => {
      this.isProvider = user.IsProvider;
      this.passwordRecoveryEnabled = user.ProviderInfo.PasswordRecoveryEnabled;
      this.providerEmail = user.BrandingSetting?.SupportEmail || user.ProviderInfo?.EmailNotificationsTo;
      this.allowedRecovery = user.ProviderInfo.PasswordRecoveryEnabled && user.IsProvider;

      if (!this.isProvider && this.passwordRecoveryEnabled && !this.ability.can('read', 'AgentLocal')) this.getPassRecoveryInfo();
    });

    this.canCheckPassword = +this.mainService?.backupVersionUpdated?.substring(0, 3) >= 792;

    this.mainService.password
      .asObservable()
      .subscribe((value) => this.stepForm.get('password').setValue(value || this.value?.password || ''));
  }

  initForm(): void {
    this.stepForm = new FormGroup<EncryptionOptionsStepValueForm>({
      isCryptWithPassword: new FormControl(false),
      password: new FormControl('', [Validators.required, this.passwordValidator.bind(this)])
    });

    this.initFormEvents();
  }

  protected initFormEvents(): void {
    let lastPassword: string;

    super.initFormEvents();

    this.stepForm
      .get('isCryptWithPassword')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((v) => this.changeIsCryptWithPasswordHandler(v));

    this.stepForm
      .get('password')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (lastPassword !== value) {
          lastPassword = value;

          this.needCheckPassword = true;
        }
      });

    this.passwordControlFacade.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.stepForm.get('password').setValue(value);

      delete this.needCheckPassword;
      delete this.invalidPassword;

      this.stepForm.get('password').updateValueAndValidity();
    });
  }

  onStepFormChange(value: EncryptionOptionsStepValue): void {
    this.value = { ...value, valid: this.stepForm.valid && (!this.needCheckPassword || !this.canCheckPassword) };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.stepForm) return;

    if (changes.backupToRestore && (this.isCreate || this.resultPoint)) {
      this.showHint = false;
      this.stepForm.get('password').reset(this.mainService.password.value || '');
    }

    if (changes.backupToRestore && this.resultPoint) this.resetIsCryptWithPassword();

    if (changes.resultPoint && this.isNBF) {
      if (
        this.canCheckPassword &&
        changes.resultPoint.previousValue &&
        changes.resultPoint.currentValue &&
        !this.needCheckPassword &&
        changes.resultPoint.previousValue.date !== changes.resultPoint.currentValue?.data
      ) {
        this.needCheckPassword = true;
        this.stepForm.updateValueAndValidity();
      }
      this.stepForm.get('isCryptWithPassword').disable();
      this.changeIsCryptWithPasswordHandler(true);
    }
  }

  updateForm(value: EncryptionOptionsStepValue): void {
    if (this.isEdit) this.InitialValue = value;

    this.stepForm.reset(value);

    this.resetIsCryptWithPassword(this.isCreate);

    const passValid = value.password && value.password !== PasswordFromPlan;

    if (passValid && this.isNBF && this.needCheckPassword) {
      this.needCheckPassword = false;
      this.stepForm.updateValueAndValidity();
    }

    if (!this.isEdit && this.mainService.initialFormData && value.password) {
      this.needCheckPassword = true;
      this.stepForm.updateValueAndValidity();
    }

    if (this.isNBF && this.isRestoreIbb && this.backupToRestore && passValid && (this.isCreate || this.resultPoint)) {
      queueMicrotask(() => {
        this.showHint = false;
        this.stepForm.get('password').reset(value.password || '');
      });
    }
  }

  forceValid(step: ForceValidType = null): void {
    if ((step.isNext || step.isSave) && (this.stepForm.invalid || (this.canCheckPassword && this.needCheckPassword))) {
      return void this.checkPassword(!!step.isSave);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  checkPassword(isSave = false): void {
    const rightVersion = this.canCheckPassword;
    const value = this.stepForm.get('password').value;

    if ((this.isNBF || this.stepForm.get('isCryptWithPassword').value) && !value) {
      return;
    }

    if (!rightVersion || !value || !this.resultPoint) {
      this.needCheckPassword = false;
      this.stepForm.get('password').updateValueAndValidity();
      return this.nextStep.emit(isSave);
    }

    this.loadInfo.emit({ loading: true });

    this.stepService
      .getRemoteCommandData(
        {
          agentType: AgentType.Backup,
          commandType: 'VerifyBackupPassword',
          params: {
            path: this.resultPoint.path,
            password: value,
            SessionId: this.mainService?.sessionId || null
          }
        },
        this.mainService.hid,
        this.needOldAsync
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (answer) => {
          this.invalidPassword = !answer?.data?.result;

          this.loadInfo.emit({ loading: false });

          this.needCheckPassword = false;

          this.stepForm.get('password').updateValueAndValidity();

          if (!this.invalidPassword) this.nextStep.emit(isSave);
          else {
            this.stepForm.get('password').markAsDirty();
            this.stepForm.get('password').updateValueAndValidity();
          }
        },
        error: () => {
          this.needCheckPassword = true;
          this.invalidPassword = true;

          this.loadInfo.emit({ loading: false });
          this.stepForm.get('password').updateValueAndValidity();
        }
      });
  }

  private changeIsCryptWithPasswordHandler(event): void {
    this.toggleFormControls(['password'], !!event);
  }

  changeTypePassword(): void {
    this.showPassword = !this.showPassword;
  }

  showPasswordHintChangeHandler(): void {
    this.showHint = !this.showHint;
  }

  passwordFocusHandler(event): void {
    if (event?.target?.value && this.InitialValue && event.target.value === this.InitialValue.password) {
      this.stepForm.get('password').setValue('');
    }
  }

  private passwordValidator(): ValidationErrors | null {
    return !this.needCheckPassword && this.invalidPassword ? { invalidPassword: true } : null;
  }

  private getPassRecoveryInfo(): void {
    this.providerService
      .getPasswordRecoveryDate()
      .pipe(take(1))
      .subscribe({ next: (result: { keyDate: string }) => (this.recoveryDate = result.keyDate) });
  }

  private resetIsCryptWithPassword(needResetCrypt = true): void {
    this.toggleFormControls(['isCryptWithPassword'], !this.isNBF);

    if (this.isNBF) return this.changeIsCryptWithPasswordHandler(true);

    if (needResetCrypt) {
      this.stepForm.get('isCryptWithPassword').setValue(false);
      this.changeIsCryptWithPasswordHandler(false);
    }
  }
}
